/**
 * @file Video.js
 * @description Video player component with custom controls using ReactPlayer.
 */

import { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';
import './Video.css';
import { logEvent } from '../../services/loggingService';
import { EventDataTypeEnum, LogEventType } from '../../utils/data/enums';

const MAX_THUMBNAIL_WIDTH = 640;
const MAX_THUMBNAIL_HEIGHT = 360;
const VIDEO_THUMBNAIL_PREVIEW_SECOND = 3;

function Video({
    url,
    caption,
    id,
    portalId,
    portalName,
    isInteractive = true,
    isViewLive = false,
    fill = false,
}) {
    const [playing, setPlaying] = useState(true);
    const [played, setPlayed] = useState(0);
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0);
    const [fastForwardInterval, setFastForwardInterval] = useState(null);
    const [rewindInterval, setRewindInterval] = useState(null);
    const [videoUrl, setVideoUrl] = useState(url);
    const [isHovered, setIsHovered] = useState(false);
    const [lightClicked, setLightClicked] = useState(false);
    const [playLogged, setPlayLogged] = useState(false);
    const [showThumbnail, setShowThumbnail] = useState(true);
    const playerRef = useRef(null);
    const [thumbnail, setThumbnail] = useState(null);

    useEffect(() => {
        async function generateThumbnail() {
            try {
                const video = document.createElement('video');
                video.crossOrigin = 'anonymous';
                video.src = videoUrl;
                video.preload = 'metadata';

                await new Promise((resolve, reject) => {
                    video.onloadedmetadata = resolve;
                    video.onerror = reject;
                });

                await new Promise((resolve, reject) => {
                    video.onloadeddata = resolve;
                    video.onerror = reject;
                });

                await new Promise(resolve => setTimeout(resolve, 100));

                const seekTime = Math.min(
                    VIDEO_THUMBNAIL_PREVIEW_SECOND,
                    video.duration,
                );

                await new Promise((resolve, reject) => {
                    video.currentTime = seekTime;
                    video.onseeked = resolve;
                    video.onerror = reject;
                });

                await new Promise(resolve => setTimeout(resolve, 100));

                const { videoWidth, videoHeight } = video;

                if (
                    !videoWidth ||
                    !videoHeight ||
                    videoWidth === 0 ||
                    videoHeight === 0
                ) {
                    console.warn(
                        'Video dimensions not available, using fallback',
                    );
                    setThumbnail(null);
                    return;
                }

                const aspectRatio = videoWidth / videoHeight;

                let canvasWidth = videoWidth;
                let canvasHeight = videoHeight;

                if (canvasWidth > MAX_THUMBNAIL_WIDTH) {
                    canvasWidth = MAX_THUMBNAIL_WIDTH;
                    canvasHeight = canvasWidth / aspectRatio;
                }

                if (canvasHeight > MAX_THUMBNAIL_HEIGHT) {
                    canvasHeight = MAX_THUMBNAIL_HEIGHT;
                    canvasWidth = canvasHeight * aspectRatio;
                }

                canvasWidth = Math.round(canvasWidth);
                canvasHeight = Math.round(canvasHeight);

                const canvas = document.createElement('canvas');
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);

                const dataURL = canvas.toDataURL('image/png');
                setThumbnail(dataURL);
            } catch (error) {
                console.error('Failed to generate thumbnail:', error);
                setThumbnail(null);
            }
        }

        if (!videoUrl) {
            return;
        }

        const timer = setTimeout(() => {
            generateThumbnail();
        }, 100);

        return () => clearTimeout(timer);
    }, [videoUrl]);

    useEffect(() => {
        setVideoUrl(url);
        setShowThumbnail(true);
        setLightClicked(false);
        setPlayed(0);
    }, [url]);

    function handlePlayPause() {
        setPlaying(!playing);
        if (!playLogged && !isViewLive) {
            logEvent(LogEventType.UserVideoPlayed, `Played video ${videoUrl}`, {
                subjectDataTypeId: EventDataTypeEnum.Container,
                subjectId: id,
                subjectName: caption || 'Untitled video',
                contextDataTypeId: EventDataTypeEnum.Portal,
                contextId: portalId,
                contextName: portalName || undefined,
            });
            setPlayLogged(true);
        }
    }

    function handleStop() {
        setPlaying(false);
        playerRef.current.seekTo(0);
    }

    function handleProgress(progress) {
        setPlayed(progress.played);
        setPlayedSeconds(progress.playedSeconds);
    }

    function handleSeek(e) {
        const seekTime = e.target.value;
        playerRef.current.seekTo(seekTime);
    }

    function handleVolumeChange(value) {
        const newVolume = parseFloat(value);
        setVolume(newVolume);
    }

    function handleFullScreen() {
        screenfull.request(playerRef.current.wrapper);
    }

    function handleFastForwardPress() {
        if (!playerRef.current) {
            return;
        }

        setFastForwardInterval(
            setInterval(() => {
                playerRef.current.seekTo(
                    playerRef.current.getCurrentTime() + 10,
                    'seconds',
                );
            }, 1000),
        );
    }

    function handleFastForwardRelease() {
        clearInterval(fastForwardInterval);
    }

    function handleRewindPress() {
        if (!playerRef.current) {
            return;
        }

        setRewindInterval(
            setInterval(() => {
                playerRef.current.seekTo(
                    playerRef.current.getCurrentTime() - 10,
                    'seconds',
                );
            }, 1000),
        );
    }

    function handleRewindRelease() {
        clearInterval(rewindInterval);
    }

    function formatTime(time) {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    function handleDuration(newDuration) {
        setDuration(newDuration);
    }

    function handleLightClick() {
        if (!isInteractive) {
            return;
        }

        setShowThumbnail(false);
        setLightClicked(true);
    }

    function handleMouseEnter() {
        if (!isInteractive) {
            return;
        }

        setIsHovered(true);
    }

    function handleMouseLeave() {
        if (!isInteractive) {
            return;
        }

        setIsHovered(false);
    }

    if (!url) {
        return null;
    }

    const shouldShowBackgroundThumbnail = showThumbnail && played === 0;

    return (
        <>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    overflow: 'clip',
                    width: '100%',
                    height: fill ? '100%' : 'auto',
                    maxWidth: '100vw',
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        aspectRatio: '16/9',
                        height: fill ? '100%' : 'auto',
                        borderRadius: '10px',
                        background: '#000',
                        maxWidth: '100vw',
                    }}
                >
                    <ReactPlayer
                        ref={playerRef}
                        className="react-player"
                        url={videoUrl}
                        light={showThumbnail}
                        playing={playing}
                        controls={false}
                        volume={volume}
                        muted={volume === 0}
                        width="100%"
                        height="100%"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundImage: shouldShowBackgroundThumbnail
                                ? `url(${thumbnail})`
                                : 'none',
                            backgroundSize: 'cover',
                            pointerEvents: isInteractive ? 'auto' : 'none',
                        }}
                        progressInterval={1000}
                        onProgress={handleProgress}
                        onDuration={handleDuration}
                        showRelatedVideos={false}
                        onClickPreview={handleLightClick}
                        preload
                    />
                    {isInteractive && isHovered && lightClicked && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                background: 'rgba(47, 43, 67, 0.6)',
                                display: 'flex',
                                padding: '4px',
                                flexDirection: 'column',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    padding: '0px 8px 0px 32px',
                                    alignItems: 'center',
                                }}
                            >
                                <input
                                    type="range"
                                    className="form-range"
                                    min={0}
                                    max={1}
                                    step="any"
                                    value={played}
                                    onChange={handleSeek}
                                    style={{
                                        flex: 1,
                                        marginRight: '10px',
                                    }}
                                />
                                <div
                                    style={{
                                        color: '#ffffff81',
                                        fontSize: '12px',
                                        marginRight: '24px',
                                    }}
                                >
                                    {formatTime(duration - playedSeconds)}
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    padding: '0px 10px',
                                    alignItems: 'center',
                                    marginTop: '-6px',
                                }}
                            >
                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                    }}
                                />
                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <i
                                        onClick={handlePlayPause}
                                        onKeyDown={e =>
                                            e.key === 'Enter' &&
                                            handlePlayPause()
                                        }
                                        role="button"
                                        tabIndex={0}
                                        className={`bi bi-${playing ? 'pause' : 'play'}-fill`}
                                        style={{
                                            fontSize: '18px',
                                            color: 'white',
                                            margin: '0px 10px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: '14px',
                                        marginRight: '24px',
                                    }}
                                >
                                    <div>
                                        <i
                                            onClick={() =>
                                                handleVolumeChange(
                                                    volume === 0 ? 1 : 0,
                                                )
                                            }
                                            onKeyDown={e =>
                                                e.key === 'Enter' &&
                                                handleVolumeChange(
                                                    volume === 0 ? 1 : 0,
                                                )
                                            }
                                            role="button"
                                            tabIndex={0}
                                            className={`bi bi-volume-${volume === 0 ? 'mute' : 'up'}-fill`}
                                            style={{
                                                fontSize: '18px',
                                                color: 'white',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <i
                                            onClick={handleFullScreen}
                                            onKeyDown={e =>
                                                e.key === 'Enter' &&
                                                handleFullScreen()
                                            }
                                            role="button"
                                            tabIndex={0}
                                            className="bi bi-fullscreen"
                                            style={{
                                                fontSize: '14px',
                                                color: 'white',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {caption && <p>{caption}</p>}
        </>
    );
}

export default Video;
